/* Progressive enhancement only. Shopify-rendered HTML is the source of truth. */
class C4Navigation extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.shell = this.querySelector('.navigation-shell');
    this.menu = this.querySelector('nav');
    this.dialog = this.querySelector('.navigation-dialog');
    this.menuButton = this.querySelector('.mobile-menu-toggle');
    this.shop = this.querySelector('.shop-disclosure');
    this.bar = this.querySelector('.nav-bar');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.categories = this.querySelector('.shop-categories');
    this.bagSlot = this.querySelector('.bag-slot');
    this.viewportMobile = matchMedia('(max-width: 1023px)');
    this.mobile = { matches: this.viewportMobile.matches };
    this.more = this.querySelector('[data-more-category]');
    const more = this.querySelector('[data-more-category]');
    more?.classList.add('nav-overflow');
    this.querySelector('[data-nav-more]').hidden = !more;
    this.querySelector('[data-nav-more]').addEventListener(
      'click',
      () => {
        if (more) more.open = true;
      },
      options,
    );
    this.hover = matchMedia('(hover: hover) and (pointer: fine)');
    if (!this.dialog.showModal) return;
    this.setAttribute('data-ready', '');
    this.querySelector('[data-shop-close]').hidden = false;
    const sync = () => this.setMode(this.viewportMobile.matches || !!this.fitCompact);
    this.menuButton.addEventListener(
      'click',
      () => {
        this.rootLevel();
        this.shop.open = true;
        this.dialog.showModal();
        this.menuButton.setAttribute('aria-expanded', 'true');
        this.animateLevel();
      },
      options,
    );
    this.dialog.addEventListener(
      'close',
      () => {
        if (this.preserveClose) {
          this.preserveClose = false;
          return;
        }
        this.menuButton.setAttribute('aria-expanded', 'false');
        this.rootLevel();
        if (this.mobile.matches && !this.cartActive) this.menuButton.focus({ preventScroll: true });
      },
      options,
    );
    this.querySelector('[data-nav-close]').addEventListener('click', () => this.close(), options);
    this.querySelector('[data-nav-back]').addEventListener(
      'click',
      () => {
        if (this.backSublevel()) return;
        const active = this.menu.querySelector('.nav-disclosure[open]');
        this.rootLevel();
        (active?.hasAttribute('data-more-category')
          ? this.querySelector('[data-nav-more]')
          : active?.querySelector('summary')
        )?.focus();
      },
      options,
    );
    this.querySelector('[data-shop-close]').addEventListener(
      'click',
      () => {
        this.close();
        this.shop.querySelector('summary').focus();
      },
      options,
    );
    this.addEventListener(
      'toggle',
      (event) => {
        if (
          event.target.matches('[data-mobile-level]') &&
          this.mobile.matches &&
          this.dialog.open
        ) {
          const parent = event.target.closest('.nav-disclosure');
          if (!parent.open) return;
          if (event.target.open) this.dialog.dataset.sublevel = event.target.dataset.mobileLevel;
          else delete this.dialog.dataset.sublevel;
          this.querySelector('[data-nav-current]').textContent =
            parent.dataset.category +
            (event.target.open ? ` › ${event.target.dataset.mobileLevel}` : '');
          if (event.target.open) event.target.querySelector('a')?.focus();
          this.animateLevel();
          return;
        }
        if (event.target === this.shop) {
          if (!this.shop.open) this.rootLevel();
          this.syncBar();
          clearTimeout(this.settleTimer);
          this.shopSettling = this.shop.open && !this.mobile.matches && !this.reducedMotion.matches;
          this.settleTimer = setTimeout(() => {
            this.shopSettling = false;
            if (this.pendingSummary?.isConnected && this.shop.open)
              this.pendingSummary.parentElement.open = true;
            this.pendingSummary = null;
          }, 330);
        }
        if (!event.target.matches('.nav-disclosure[open]')) return;
        this.querySelectorAll('.nav-disclosure[open]').forEach((details) => {
          if (details !== event.target) details.open = false;
        });
        if (this.dialog.open) {
          this.dialog.dataset.level = 'category';
          this.querySelector('[data-nav-current]').textContent = event.target.dataset.category;
          event.target.querySelector('.nav-panel a')?.focus();
          this.animateLevel();
        } else {
          this.placePanel(event.target);
        }
      },
      { ...options, capture: true },
    );
    this.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('.shop-toggle') && !this.mobile.matches) {
          event.preventDefault();
          this.shop.open = !this.shop.open;
          this.syncBar();
        }
        const summary = event.target.closest('.nav-disclosure > summary');
        if (summary && !this.mobile.matches && this.hover.matches && event.detail > 0) {
          event.preventDefault();
          summary.parentElement.open = true;
        }
        if (
          this.dialog.open &&
          event.target.closest('a') &&
          !event.target.closest('[data-open-cart]')
        )
          this.close();
      },
      options,
    );
    this.addEventListener(
      'pointerover',
      (event) => {
        clearTimeout(this.closeTimer);
        if (this.mobile.matches || !this.hover.matches || event.pointerType === 'touch') return;
        const summary = event.target.closest('.nav-disclosure > summary');
        if (summary) {
          if (this.shopSettling) this.pendingSummary = summary;
          else summary.parentElement.open = true;
        }
      },
      options,
    );
    this.menu.addEventListener(
      'pointerleave',
      (event) => {
        if (
          this.mobile.matches ||
          !this.hover.matches ||
          this.cartActive ||
          this.contains(event.relatedTarget)
        )
          return;
        this.closeTimer = setTimeout(() => {
          if (this.cartActive) return;
          if (
            this.menu.contains(document.activeElement) &&
            document.activeElement.matches(':focus-visible')
          )
            return;
          this.rootLevel();
        }, 200);
      },
      options,
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          const nested = event.target.closest('.nav-subgroup[open]');
          if (nested) {
            nested.open = false;
            nested.querySelector('summary').focus();
          } else if (this.dialog.open && this.dialog.dataset.sublevel) {
            this.backSublevel();
          } else if (this.dialog.open && this.dialog.dataset.level === 'category') {
            const active = this.menu.querySelector('.nav-disclosure[open]');
            this.rootLevel();
            (active?.hasAttribute('data-more-category')
              ? this.querySelector('[data-nav-more]')
              : active?.querySelector('summary')
            )?.focus();
          } else if (this.dialog.open) this.close();
          else if (this.querySelector('details[open]')) {
            this.close();
            this.shop.querySelector('summary').focus();
          }
          event.preventDefault();
          event.stopPropagation();
        }
        if (event.key === 'Tab' && this.dialog.open) {
          const targets = [
            ...this.dialog.querySelectorAll('a[href], button, summary, [tabindex="0"]'),
          ].filter((node) => !node.disabled && node.getClientRects().length);
          const first = targets[0],
            last = targets.at(-1);
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }
      },
      options,
    );
    document.addEventListener(
      'click',
      (event) => {
        if (
          !this.cartActive &&
          !this.contains(event.target) &&
          !event.target.closest('c4-cart, [data-open-cart]')
        )
          this.close();
      },
      options,
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (
          !this.cartActive &&
          !this.dialog.open &&
          event.relatedTarget &&
          !this.contains(event.relatedTarget) &&
          !event.relatedTarget.closest('c4-cart')
        )
          this.close();
      },
      options,
    );
    const scroll = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        /* Condense as the bar's bottom edge meets the first banner. */
        const hero = document.querySelector('.campaign-section');
        /* Two states, moved by a CSS transition, never per frame: Safari scrolls ahead of scripts. */
        const notice = document.querySelector('.announcement');
        const step = notice && !/fixed|absolute/.test(getComputedStyle(notice).position);
        const announce =
          step && notice.getBoundingClientRect().bottom > 0 ? notice.offsetHeight : 0;
        if (announce !== this.announce) {
          this.announce = announce;
          document.documentElement.style.setProperty('--announce', `${announce}px`);
        }
        this.navTop ??= parseFloat(getComputedStyle(this).getPropertyValue('--nav-top')) || 16;
        const barBottom = this.navTop + announce + 42;
        const condensed = hero
          ? barBottom >= hero.getBoundingClientRect().top
          : window.scrollY > this.offsetHeight - 25;
        if (condensed !== this.hasAttribute('data-condensed')) {
          this.toggleAttribute('data-condensed', condensed);
          this.syncBar();
        }
        /* A framed page's logo (page-frame.css): hanging off the top section ([data-logo-hang]) while it is under
           the band from the small mark's top to the big one's middle; past it, small. */
        const frame = this.dataset.logoFrame;
        if (frame === 'hanging' || frame === 'small') {
          const over = [...document.querySelectorAll('[data-logo-hang]')].some((el) => {
            const { top, bottom } = el.getBoundingClientRect();
            return top <= barBottom + 140 && bottom >= barBottom + 16;
          });
          const float = this.dataset.logoScroll === 'float' && !this.viewportMobile.matches;
          this.toggleAttribute('data-logo-small', float ? !over : condensed);
          /* Past its sections it parks on their foot (page-frame.css). */
          let park, gone;
          if (!this.viewportMobile.matches && (frame === 'small' || float)) {
            this.probe ??= this.appendChild(document.createElement('i'));
            this.probe.className = 'logo-probe';
            const rest = this.probe.getBoundingClientRect();
            const foot = this.navTop + announce + rest.height + rest.width;
            const runs = [];
            for (const el of document.querySelectorAll('[data-logo-hang], [data-logo-lane]')) {
              const box = el.getBoundingClientRect();
              if (!box.height) continue;
              const rail = el.querySelector('[data-logo-rail]');
              const hang = el.dataset.logoHang != null && frame === 'hanging';
              const end = rail
                ? rail.getBoundingClientRect().bottom
                : box.bottom - (hang ? 0 : parseFloat(getComputedStyle(el).paddingBottom));
              const line = hang ? foot - rest.width - 8 : foot;
              const last = runs.at(-1);
              if (last && box.top - last.end < 320) Object.assign(last, { end, line });
              else runs.push({ top: box.top, end, line });
            }
            const riding = runs.some((r) => r.top <= foot && r.end >= r.line);
            const passed = !riding && runs.filter((r) => r.end < r.line).at(-1);
            const shift = passed ? passed.line - passed.end : 1e4;
            park = !!passed;
            gone = !riding && shift > foot + 120;
            if (park)
              this.style.setProperty(
                '--logo-park',
                `${-shift - this.getBoundingClientRect().top}px`,
              );
          }
          this.toggleAttribute('data-logo-park', park);
          this.toggleAttribute('data-logo-gone', gone);
        }
        this.scrollFrame = 0;
      });
    };
    window.addEventListener('scroll', scroll, { ...options, passive: true });
    this.viewportMobile.addEventListener('change', sync, options);
    this.resizeObserver = new ResizeObserver(() => this.syncBar());
    this.resizeObserver.observe(this.bar);
    this.resizeObserver.observe(this.querySelector('.site-header'));
    this.menu.addEventListener('transitionend', () => this.syncBar(), options);
    /* A dropdown's brands row keeps to one line (nav-edge.css): once shown, and as it narrows or widens, the brands
       that would wrap are hidden, the menu's first kept. */
    this.brands = new ResizeObserver((entries) =>
      entries.forEach(({ target, contentRect }) => {
        if (!contentRect.width || target.fitted === contentRect.width) return;
        target.fitted = contentRect.width;
        const items = [...target.children];
        items.forEach((item) => (item.hidden = false));
        items.forEach((item) => (item.hidden = item.offsetTop > items[0].offsetTop));
      }),
    );
    this.querySelectorAll('.nav-list .nav-brands ul').forEach((list) => this.brands.observe(list));
    window.addEventListener('resize', () => this.syncBar(), options);
    this.modeInitialized = false;
    sync();
    scroll();
  }
  setMode(compact) {
    if (this.modeInitialized && compact === this.mobile.matches) return;
    const wasOpen =
      this.dialog.open || this.resumeDialog || (!this.mobile.matches && this.shop.open);
    if (this.cartActive) this.resumeDialog = compact && wasOpen;
    if (this.dialog.open) {
      this.preserveClose = true;
      this.dialog.close();
    }
    this.mobile.matches = compact;
    this.modeInitialized = true;
    this.toggleAttribute('data-compact', compact);
    this.menuButton.hidden = !compact;
    if (compact) {
      this.dialog.querySelector('.mobile-nav-content').append(this.menu);
      this.shop.open = true;
      if (this.more) this.querySelector('.nav-list').append(this.more.parentElement);
      if (wasOpen && !this.cartActive) {
        this.dialog.showModal();
        this.menuButton.setAttribute('aria-expanded', 'true');
        const active = this.menu.querySelector('.nav-disclosure[open]');
        if (active) {
          this.dialog.dataset.level = 'category';
          this.querySelector('[data-nav-current]').textContent = active.dataset.category;
        }
      }
    } else {
      this.shell.append(this.menu);
      this.shop.open = wasOpen;
      if (this.more) this.querySelector('.nav-primary').append(this.more.parentElement);
      this.menuButton.setAttribute('aria-expanded', 'false');
    }
    this.syncBar();
  }
  syncBar() {
    // Re-evaluate a content-driven compact layout when width or font size changes.
    if (this.fitCompact && !this.viewportMobile.matches) {
      const font = parseFloat(getComputedStyle(this.shop.querySelector('summary')).fontSize);
      if (innerWidth > (this.fitWidth * font) / this.fitFont + 24) {
        this.fitCompact = false;
        this.setMode(false);
        return;
      }
    }
    this.categories.inert = !this.mobile.matches && !this.shop.open;
    this.bagSlot.inert = this.mobile.matches || !this.hasAttribute('data-condensed');
    if (this.mobile.matches) return;
    const width = this.bar.getBoundingClientRect().width;
    const barRect = this.bar.getBoundingClientRect();
    const categoryRect = this.categories.getBoundingClientRect();
    const clipRect = this.categories.parentElement.getBoundingClientRect();
    this.bar.style.setProperty('--shop-glass-left', `${categoryRect.left - barRect.left}px`);
    this.bar.style.setProperty(
      '--shop-glass-width',
      `${Math.min(categoryRect.width, clipRect.width)}px`,
    );
    const brand = this.querySelector('.brand');
    const actions = this.querySelector('.header-actions');
    const left = brand.offsetLeft + brand.querySelector('.mark').offsetWidth + 24;
    const right = innerWidth - actions.offsetWidth - 20 - 24;
    const side = Math.max(left, innerWidth - right);
    if (width > innerWidth - 2 * side + 1) {
      this.fitCompact = true;
      this.fitWidth = width + 2 * side;
      this.fitFont = parseFloat(getComputedStyle(this.shop.querySelector('summary')).fontSize);
      this.setMode(true);
      return;
    }
    this.querySelectorAll('.nav-disclosure[open]').forEach((details) => this.placePanel(details));
  }
  suspendForCart() {
    this.cartActive = true;
    clearTimeout(this.closeTimer);
    clearTimeout(this.settleTimer);
    this.pendingSummary = null;
    this.resumeDialog = this.dialog.open;
    if (this.resumeDialog) {
      this.preserveClose = true;
      this.dialog.close();
    }
  }
  resumeAfterCart() {
    this.cartActive = false;
    if (this.resumeDialog && this.mobile.matches) {
      this.dialog.showModal();
      this.menuButton.setAttribute('aria-expanded', 'true');
      this.resumeDialog = false;
      return true;
    }
    this.resumeDialog = false;
    this.syncBar();
    return false;
  }
  placePanel(details) {
    if (this.mobile.matches) return;
    const panel = details.querySelector('.nav-panel');
    const bar = this.bar.getBoundingClientRect();
    const tab = details.querySelector('summary').getBoundingClientRect();
    const width = panel.offsetWidth;
    const shift = parseFloat(getComputedStyle(this.shell).marginLeft) || 0;
    const center = tab.x - shift + tab.width / 2;
    const minimum = this.hasAttribute('data-condensed') ? 12 : 155;
    const left = Math.max(
      12,
      Math.min(Math.max(minimum, center - width / 2), innerWidth - width - 12),
    );
    panel.style.setProperty('--nav-left', `${left}px`);
    panel.style.setProperty('--nav-panel-top', `${bar.bottom + 10}px`);
    panel.style.setProperty('--nav-translate', '0px');
    panel.style.setProperty('--nav-origin', `${center - left}px`);
  }
  animateLevel() {
    if (!this.dialog.open || this.reducedMotion.matches) return;
    const selector =
      this.dialog.dataset.level === 'category'
        ? '.nav-disclosure[open] .nav-column li, .nav-disclosure[open] .nav-features'
        : '.nav-list > li, .nav-primary > li';
    const easing = getComputedStyle(this).getPropertyValue('--nav-spring').trim();
    [...this.menu.querySelectorAll(selector)]
      .filter((node) => node.getClientRects().length)
      .forEach((node, index) => {
        node.getAnimations().forEach((animation) => animation.cancel());
        node.animate(
          [
            { opacity: 0, transform: 'translateY(10px) scale(.96)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 550, delay: Math.min(index, 8) * 45, easing, fill: 'backwards' },
        );
      });
  }
  backSublevel() {
    const active = this.menu.querySelector('[data-mobile-level][open]');
    if (!active || !this.mobile.matches) return false;
    active.open = false;
    delete this.dialog.dataset.sublevel;
    active.querySelector('summary').focus();
    return true;
  }
  rootLevel() {
    this.pendingSummary = null;
    delete this.dialog.dataset.sublevel;
    this.querySelectorAll('.nav-disclosure[open], .nav-subgroup[open]').forEach((details) => {
      details.open = false;
    });
    this.dialog.dataset.level = 'root';
    this.querySelector('[data-nav-current]').textContent = '';
    this.animateLevel();
  }
  close() {
    this.pendingSummary = null;
    clearTimeout(this.closeTimer);
    if (this.dialog.open) this.dialog.close();
    this.rootLevel();
    if (!this.mobile.matches) this.shop.open = false;
  }
  disconnectedCallback() {
    this.abort?.abort();
    clearTimeout(this.closeTimer);
    clearTimeout(this.settleTimer);
    this.resizeObserver?.disconnect();
    this.brands?.disconnect();
    this.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
    cancelAnimationFrame(this.scrollFrame);
  }
}

class C4Cart extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.dialog = this.querySelector('dialog');
    this.busy = false;
    this.root = this.dataset.root.replace(/\/?$/, '/');
    document.addEventListener(
      'click',
      (event) => {
        const open = event.target.closest('[data-open-cart]');
        if (
          open &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.shiftKey &&
          !event.altKey &&
          event.button === 0 &&
          this.dialog.showModal
        ) {
          event.preventDefault();
          this.opener = open;
          this.open();
          if (!this.busy) this.refresh();
        }
        const stepper = event.target.closest('[data-quantity-step]');
        if (stepper && !this.busy) {
          const input = stepper.closest('.quantity-control').querySelector('[data-quantity]');
          const quantity =
            Number(input.value) + Number(input.step || 1) * Number(stepper.dataset.quantityStep);
          const min = Number(input.dataset.min || 1);
          if (quantity < min) this.mutate('change', { id: input.dataset.key, quantity: 0 }, input);
          else if (!input.max || quantity <= Number(input.max))
            this.mutate('change', { id: input.dataset.key, quantity }, input);
        }
      },
      options,
    );
    document.addEventListener(
      'input',
      (event) => {
        if (event.target.matches('[data-cart-note]')) this.noteDraft = event.target.value;
      },
      options,
    );
    document.addEventListener(
      'change',
      (event) => {
        const plan = event.target.closest('[data-cart-plan]');
        if (plan && !this.busy) {
          const { value, id } = plan;
          const lines = [
            ...plan.closest('[data-plan-group]').querySelectorAll('[data-plan-ids]'),
          ].map((line) => ({
            id: line.dataset.lineKey,
            quantity: Number(line.dataset.planQuantity),
            selling_plan: value ? Number(value) : null,
          }));
          (async () => {
            for (const data of lines)
              await this.mutate('change', data, document.getElementById(id) || plan);
          })();
          return;
        }
        const note = event.target.closest('[data-cart-note]');
        if (note && !this.busy) {
          this.noteDraft = note.value;
          this.mutate('update', { note: note.value }, note);
          return;
        }
        const input = event.target.closest('[data-quantity]');
        if (!input || this.busy || !input.checkValidity()) return;
        this.mutate('change', { id: input.dataset.key, quantity: Number(input.value) }, input);
      },
      options,
    );
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form.matches('[data-product-form], [data-cart-form]')) return;
        if (this.busy) {
          event.preventDefault();
          return;
        }
        if (form.matches('[data-cart-form]')) return; // Native update / checkout fallback.
        event.preventDefault();
        this.mutate('add', new FormData(form), event.submitter, form);
      },
      options,
    );
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted && !this.busy) this.refresh();
      },
      options,
    );
  }
  sectionIds() {
    return [
      ...new Set(
        [...document.querySelectorAll('[data-cart-section]')].map(
          (node) => node.dataset.cartSection,
        ),
      ),
    ];
  }
  setBusy(busy) {
    this.busy = busy;
    document.querySelectorAll('[data-cart-section], c4-product').forEach((node) => {
      node.setAttribute('aria-busy', String(busy));
      node.querySelectorAll('button, input, select, textarea').forEach((control) => {
        if (busy && !control.disabled) {
          control.dataset.cartDisabled = '';
          control.disabled = true;
        } else if (!busy && 'cartDisabled' in control.dataset) {
          control.disabled = false;
          delete control.dataset.cartDisabled;
        }
      });
    });
  }
  render(sections, unchanged) {
    const replacements = this.sectionIds().map((id) => {
      if (typeof sections?.[id] !== 'string') throw new Error(this.dataset.error);
      const parsed = new DOMParser().parseFromString(sections[id], 'text/html');
      const fresh = [...parsed.querySelectorAll('[data-cart-section]')].find(
        (node) => node.dataset.cartSection === id,
      );
      const current = [...document.querySelectorAll('[data-cart-section]')].find(
        (node) => node.dataset.cartSection === id,
      );
      if (!fresh || !current) throw new Error(this.dataset.error);
      return [current, fresh];
    });
    replacements.forEach(([current, fresh]) => {
      if (unchanged && current.innerHTML === fresh.innerHTML) return;
      const noteOpen = current.querySelector('.cart-note')?.open;
      current.dispatchEvent(new Event('shopify:section:unload', { bubbles: true }));
      current.replaceWith(fresh);
      if (this.noteDraft !== undefined) {
        const note = fresh.querySelector('[data-cart-note]');
        if (note) note.value = this.noteDraft;
      }
      if (noteOpen && fresh.querySelector('.cart-note'))
        fresh.querySelector('.cart-note').open = true;
      fresh.dispatchEvent(new Event('shopify:section:load', { bubbles: true }));
    });
    const count = this.querySelector('[data-item-count]')?.dataset.itemCount;
    if (count !== undefined)
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = count;
      });
  }
  async sections() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.sectionIds().join(','));
    const response = await fetch(url, {
      signal: AbortSignal.timeout(15000),
    });
    if (!response.ok) throw new Error(this.dataset.error);
    return response.json();
  }
  async fetchSections() {
    this.render(await this.sections());
  }
  showError(message, form) {
    const target =
      form?.querySelector('[data-product-error]') ||
      (this.dialog.open ? this.dialog : document.querySelector('main')).querySelector(
        '[data-cart-error]',
      );
    if (target) {
      target.textContent = message;
      target.hidden = false;
    }
    document.querySelector('[data-cart-status]').textContent = message;
  }
  /* On opening the bag: Shopify's current cart, without disabling anything meanwhile. A change made before it
     arrives wins (the stale answer is dropped), and an unchanged section is left as it is, mid-slide. */
  async refresh() {
    const token = (this.refreshing = {});
    const regions = () => document.querySelectorAll('[data-cart-section]');
    regions().forEach((node) => node.setAttribute('aria-busy', 'true'));
    try {
      const sections = await this.sections();
      if (this.refreshing === token) this.render(sections, true);
    } catch {
      if (this.refreshing === token) this.showError(this.dataset.refreshError);
    } finally {
      if (this.refreshing === token) {
        this.refreshing = null;
        regions().forEach((node) => node.setAttribute('aria-busy', 'false'));
      }
    }
  }
  async mutate(action, data, trigger, form) {
    if (this.busy) return;
    this.refreshing = null;
    const focusKey = trigger?.dataset.key;
    const wasQuantity = trigger?.matches('[data-quantity]');
    const wasPlan = trigger?.matches('[data-cart-plan]');
    const wasNote = trigger?.matches('[data-cart-note]');
    const wasInDialog = this.dialog.open;
    const activeSection = trigger?.closest('[data-cart-section]')?.dataset.cartSection;
    const ids = this.sectionIds();
    const path = window.location.pathname;
    let options;
    if (data instanceof FormData) {
      data.set('sections', ids.join(','));
      data.set('sections_url', path);
      options = { body: data };
    } else {
      options = {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, sections: ids, sections_url: path }),
      };
    }
    document.querySelectorAll('[data-cart-error], [data-product-error]').forEach((node) => {
      node.hidden = true;
    });
    this.setBusy(true);
    let succeeded = false;
    try {
      const response = await fetch(`${this.root}cart/${action}.js`, {
        ...options,
        method: 'POST',
        signal: AbortSignal.timeout(15000),
      });
      succeeded = response.ok;
      let result;
      try {
        result = await response.json();
      } catch {
        if (!succeeded) throw new Error(this.dataset.error);
        await this.fetchSections();
        if (action === 'add') {
          this.opener = trigger;
          if (!this.dialog.open) this.open();
        }
        if (action === 'update' && Object.hasOwn(data, 'note')) this.noteDraft = undefined;
        document.querySelector('[data-cart-status]').textContent = this.dataset.updated;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action } }));
        return;
      }
      if (!response.ok)
        throw new Error(
          typeof result.description === 'string' ? result.description : this.dataset.error,
        );
      succeeded = true;
      if (action === 'update' && Object.hasOwn(data, 'note')) this.noteDraft = undefined;
      try {
        this.render(result.sections);
      } catch {
        await this.fetchSections();
      } // A successful mutation may return null sections.
      if (action === 'add') {
        this.opener = trigger;
        if (!this.dialog.open) this.open();
      }
      document.querySelector('[data-cart-status]').textContent = this.dataset.updated;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action } }));
    } catch (error) {
      // Never retry a write: a lost response can still mean Shopify accepted it.
      if (succeeded) {
        window.location.assign(`${this.root}cart`);
        return;
      }
      try {
        await this.fetchSections();
      } catch {
        /* Keep the native cart link available. */
      }
      this.showError(
        error.name === 'TimeoutError' || error instanceof TypeError || error instanceof SyntaxError
          ? this.dataset.error
          : error.message,
        form,
      );
    } finally {
      this.setBusy(false);
      if (action !== 'add' && this.dialog.open === wasInDialog) {
        const region = [...document.querySelectorAll('[data-cart-section]')].find(
          (node) => node.dataset.cartSection === activeSection,
        );
        const line = [...(region?.querySelectorAll('[data-line-key]') || [])].find(
          (node) => node.dataset.lineKey === focusKey,
        );
        const next =
          (wasNote
            ? region?.querySelector('[data-cart-note]')
            : wasPlan
              ? region?.querySelector('[data-cart-plan]')
              : line?.querySelector('[data-quantity]')) ||
          region?.querySelector('[data-quantity], .button') ||
          (wasInDialog ? this.dialog.querySelector('#CartTitle') : null);
        next?.focus();
      }
    }
  }
  /* The drawer is a side panel (assets/side-panel.js): its motion, the page's push, focus and closing are shared. */
  open() {
    if (window.C4SidePanel) window.C4SidePanel.open(this.dialog, { opener: this.opener });
    else if (!this.dialog.open) this.dialog.showModal();
  }
  disconnectedCallback() {
    this.abort?.abort();
  }
}
if (!customElements.get('c4-navigation')) customElements.define('c4-navigation', C4Navigation);
if (!customElements.get('c4-cart')) customElements.define('c4-cart', C4Cart);

/* Search loads on first use: the first search link pressed (or hovered or focused) fetches search.js, then the
   dialog opens; after that search.js handles the links itself. If it cannot load, the link goes to the search page. */
{
  let loading;
  const search = () => document.querySelector('c4-search[data-script]');
  const load = () =>
    (loading ||= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = search().dataset.script;
      script.onload = () => customElements.whenDefined('c4-search').then(resolve);
      script.onerror = reject;
      document.head.append(script);
    }));
  const trigger = (event) =>
    !customElements.get('c4-search') &&
    window.HTMLDialogElement &&
    search() &&
    event.target.closest?.('[data-open-search]');
  for (const type of ['pointerover', 'focusin'])
    document.addEventListener(type, (event) => trigger(event) && load().catch(() => {}));
  document.addEventListener('click', (event) => {
    const link = trigger(event);
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    load().then(
      () => search().open(link),
      () => (location.href = link.href),
    );
  });
}
